import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Feather, Lock, ArrowLeft, Check, AlertCircle } from 'lucide-react';

import { API } from '../config';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-playfair text-3xl text-charcoal mb-4">Password Reset!</h1>
          <p className="font-lora text-charcoal-muted mb-8">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="btn-primary"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-playfair text-3xl text-charcoal mb-4">Invalid Link</h1>
          <p className="font-lora text-charcoal-muted mb-8">{error}</p>
          <Button 
            onClick={() => navigate('/forgot-password')}
            className="btn-primary"
          >
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center p-8">
      <div className="w-full max-w-md animate-fade-in">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-lora">Back to login</span>
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Feather className="w-8 h-8 text-wine" />
          <span className="font-playfair text-2xl text-charcoal">Unspoken Hearts</span>
        </div>

        <h1 className="font-playfair text-4xl text-charcoal mb-2">Set New Password</h1>
        <p className="font-lora text-charcoal-muted mb-8">
          Enter your new password below.
        </p>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-lora text-red-700 text-sm">{error}</p>
          </div>
        )}

        <Card className="card-letter">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="font-lora text-charcoal">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-underline pl-10 py-3"
                  placeholder="••••••••"
                  minLength={6}
                  required
                  data-testid="password-input"
                />
              </div>
              <p className="font-lora text-xs text-charcoal-light">Minimum 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-lora text-charcoal">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-underline pl-10 py-3"
                  placeholder="••••••••"
                  minLength={6}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full justify-center"
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
